import React from 'react';
import { ChevronRightIcon, LockIcon } from 'lucide-react';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
import { settingsSections } from '../../data/settingsNav';
import { SettingsSection } from '../../types/ui';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyNote } from '../../components/ui/Panel';
import { MembersSettings } from '../../components/settings/MembersSettings';
import { TasksSettings } from '../../components/settings/TasksSettings';
import { GoalsSettings } from '../../components/settings/GoalsSettings';
import { AnimalsSettings } from '../../components/settings/AnimalsSettings';
import { PointsSettings } from '../../components/settings/PointsSettings';
import { CalendarSettings, EveningSettings, HistorySettings, ModulesSettings } from '../../components/settings/MiscSettings';
const bodies: Record<SettingsSection, () => JSX.Element> = {
  clani: MembersSettings,
  naloge: TasksSettings,
  cilji: GoalsSettings,
  zivali: AnimalsSettings,
  tocke: PointsSettings,
  koledar: CalendarSettings,
  vecer: EveningSettings,
  moduli: ModulesSettings,
  zgodovina: HistorySettings
};
export function SettingsPage({
  section
}: {
  section?: SettingsSection;
}) {
  const {
    isAdmin,
    family,
    db
  } = useAppState();
  const {
    openSub
  } = useUi();
  if (!isAdmin) {
    return <div data-id="element-1591" data-mp="37enk3fpft5sk">
        <PageHeader title="Nastavitve družine" onBack={() => openSub(null)} data-id="element-1592" data-mp="37eduvuc7v2nx" />
        <EmptyNote data-id="element-1593" data-mp="32fanw4coemdb">
          <LockIcon className="mx-auto mb-2 h-5 w-5" aria-hidden="true" data-id="element-1594" data-mp="32esudfheem93" />
          Nastavitve družine ureja skrbnik.
        </EmptyNote>
      </div>;
  }
  const current = settingsSections.find(s => s.id === section);
  const Body = section ? bodies[section] : null;
  const planned = db.changelog.filter(c => c.familyId === family.id && c.status === 'nacrtovano').length;
  return <div data-id="element-1595" data-mp="1hyo2da7r681b">
      <PageHeader title={current ? current.label : 'Nastavitve družine'} subtitle={current ? current.hint : `Družina ${family.name} · spremembe veljajo samo za to družino`} onBack={() => section ? openSub({
      kind: 'settings'
    }) : openSub(null)} backLabel={section ? 'Nastavitve' : 'Nazaj'} data-id="element-1596" data-mp="1hy609wyeszlh" />
      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-8" data-id="element-1597" data-mp="1afevpoqmsnfy">
        <nav aria-label="Področja nastavitev" className={section ? 'hidden lg:block' : ''} data-id="element-1598" data-mp="1efyq4kjtlcye">
          <ul className="space-y-1.5" data-id="element-1599" data-mp="10otwq071pgwi">
            {settingsSections.map(({
            id,
            label,
            hint,
            icon: Icon
          }) => <li key={id} data-id="element-1600" data-mp="15i69nd2d3z9f">
                <button type="button" onClick={() => openSub({
              kind: 'settings',
              section: id
            })} aria-current={section === id ? 'page' : undefined} className={`flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors duration-150 ${section === id ? 'bg-accent-soft text-accent-ink' : 'bg-surface hover:bg-line/40'}`} data-id="element-1601" data-mp="15ieqfg1lvopw">
                  <Icon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-1602" data-mp="33si7vz7azmm" />
                  <span className="min-w-0 flex-1" data-id="element-1603" data-mp="3ualgos2m0kng">
                    <span className="block text-sm font-bold" data-id="element-1604" data-mp="3ubwrvcydwuol">{label}</span>
                    <span className="block truncate text-xs text-muted" data-id="element-1605" data-mp="c58pvvymsnn5">{id === 'zgodovina' && planned ? `${planned} načrtovanih sprememb` : hint}</span>
                  </span>
                  <ChevronRightIcon className="h-4 w-4 text-muted lg:hidden" aria-hidden="true" data-id="element-1606" data-mp="m2wm6qbvy37f" />
                </button>
              </li>)}
          </ul>
        </nav>
        <div className="min-w-0" data-id="element-1607" data-mp="h28vk5rkjodq">
          {Body ? <Body data-id="element-1608" data-mp="vroamohqm5dl" /> : <div className="hidden rounded-3xl bg-canvas p-8 text-center text-sm text-muted lg:block" data-id="element-1609" data-mp="vuslg4ppjzd8">Izberi področje na levi. Vse vrednosti so začetne nastavitve te družine in jih lahko spremeniš.</div>}
        </div>
      </div>
    </div>;
}